// ─── Universal Print Helper (bypasses browser popup blockers) ─────────
// Renders the given HTML string into a hidden iframe and triggers
// window.print() on that iframe. This works reliably on:
//   • Windows Chrome / Edge / Firefox (bypasses "popup blocked")
//   • macOS Safari
//   • Android Chrome (uses OS print service / Mopria / Brother iPrint)
//   • Any wired/USB printer (Brother HL-L2350, MFC-L2710, etc.)
//
// Why iframe instead of window.open?
//   window.open(...) is aggressively blocked by browsers unless it was
//   fired inside a *direct* user gesture; even then Chrome/Edge on
//   Windows sometimes swallow it. An iframe is an in-page element —
//   never blocked.

/**
 * Print an HTML document reliably.
 * @param {string} html — full HTML string (including <!doctype html>...</html>)
 * @param {object} opts — { title?: string, autoclose?: boolean }
 */
export const printHTML = (html, opts = {}) => {
  const { autoclose = true } = opts;

  // Remove any leftover print iframe from a previous run
  document.querySelectorAll('iframe[data-print-frame="1"]').forEach((el) => el.remove());

  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-print-frame", "1");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument || (win && win.document);
  if (!doc) {
    try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
    alert("تعذّر تجهيز صفحة الطباعة. حاول مرة ثانية.");
    return;
  }

  try {
    doc.open();
    doc.write(html);
    doc.close();
  } catch (e) {
    try { document.body.removeChild(iframe); } catch (err) { /* ignore */ }
    alert("تعذّرت كتابة صفحة الطباعة: " + (e && e.message ? e.message : e));
    return;
  }

  const cleanup = () => {
    if (!autoclose) return;
    // Give the print job time to spool before removing the iframe.
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
    }, 15000);
  };

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } catch (e) {
      alert("فشلت الطباعة: " + (e && e.message ? e.message : e));
    } finally {
      cleanup();
    }
  };

  // Wait for iframe DOM + images + fonts before triggering print.
  // - No pending images → open dialog after fonts settle (or 200ms max).
  // - Some pending images → wait for each load/error event, capped at 3s.
  // This removes the old blanket 400ms delay for image-less docs like
  // customer statements, and prevents "stuck forever" cases where an
  // iframe.onload handler never fires because the iframe finished
  // loading before we could attach the handler.
  const runPrint = () => {
    const imgs = Array.from(doc.images || []);
    const pending = imgs.filter((img) => !img.complete);

    if (pending.length === 0) {
      // Only wait for fonts to be ready (max 300ms).
      const fontsPromise = (doc.fonts && doc.fonts.ready) || Promise.resolve();
      Promise.race([
        fontsPromise,
        new Promise((r) => setTimeout(r, 300)),
      ]).finally(() => setTimeout(doPrint, 30));
      return;
    }

    // Wait for every pending image to finish (either load or error) — but
    // never block the print dialog for more than 3s total.
    let remaining = pending.length;
    const oneDone = () => {
      if (--remaining <= 0) setTimeout(doPrint, 30);
    };
    pending.forEach((img) => {
      img.addEventListener("load", oneDone, { once: true });
      img.addEventListener("error", oneDone, { once: true });
    });
    setTimeout(doPrint, 3000);
  };

  // Kick off runPrint as soon as the iframe DOM is at least "interactive".
  // Poll every 30ms as a safety net (some browsers don't reliably fire
  // iframe.onload for document.write'd iframes).
  const kick = () => {
    if (doc.readyState === "complete" || doc.readyState === "interactive") {
      runPrint();
      return true;
    }
    return false;
  };
  if (!kick()) {
    iframe.onload = kick;
    let ticks = 0;
    const poll = setInterval(() => {
      ticks += 1;
      if (printed || kick() || ticks > 100) clearInterval(poll);
    }, 30);
  }
};

/**
 * Wrap raw <body> content into a full HTML document with print-friendly
 * defaults (RTL, Arabic font stack, A4 page, sane margins). Use this when
 * you just want to print a snippet without building the whole page.
 */
export const wrapPrintHTML = (bodyHTML, opts = {}) => {
  const { title = "جملتي", extraStyles = "", pageSize = "A4", margin = "12mm" } = opts;
  return `<!doctype html>
<html lang="ar" dir="rtl">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<style>
@page { size: ${pageSize}; margin: ${margin}; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: 'Tahoma', 'Cairo', 'Segoe UI', 'Arial', sans-serif;
  color: #000; background: #fff;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
@media print {
  html, body { margin: 0 !important; }
}
${extraStyles}
</style>
</head>
<body>
${bodyHTML}
</body>
</html>`;
};

export default { printHTML, wrapPrintHTML };
